/**
 * RaceLeaderboard Component
 *
 * Live leaderboard during the race showing participant rankings.
 * Ranked by cards completed (most = highest rank).
 * Simplified view: Placement, Name, and Progress bar with app-colored gradient.
 */

import { useState, useEffect } from 'react';
import type { RaceRoom, LeaderboardEntry } from '@/types/race';
import { getAppById } from '@/config/apps';
import styles from './Race.module.css';

interface RaceLeaderboardProps {
  room: RaceRoom | null;
  leaderboard: LeaderboardEntry[];
  onEndRace: () => void;
}

export function RaceLeaderboard({ room, leaderboard, onEndRace }: RaceLeaderboardProps) {
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for elapsed time
  useEffect(() => {
    if (!room?.race_start_time) return;

    // Ensure the timestamp is parsed as UTC (Supabase may return it without 'Z' suffix)
    const timeStr = room.race_start_time.endsWith('Z')
      ? room.race_start_time
      : room.race_start_time + 'Z';
    const startTime = new Date(timeStr).getTime();

    // Calculate immediately, don't wait for first interval tick
    const now = Date.now();
    setElapsedTime(Math.floor((now - startTime) / 1000));

    const interval = setInterval(() => {
      const now = Date.now();
      setElapsedTime(Math.floor((now - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [room?.race_start_time]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const finishedCount = leaderboard.filter((e) => e.isFinished).length;
  const totalCount = leaderboard.length;

  return (
    <div className={styles.leaderboardContainer}>
      <div className={styles.leaderboardHeader}>
        <h1 className={styles.pageTitle}>Race in Progress</h1>
        <div className={styles.raceStats}>
          <div className={styles.raceStat}>
            <span className={styles.raceStatValue}>{formatTime(elapsedTime)}</span>
            <span className={styles.raceStatLabel}>Elapsed</span>
          </div>
          <div className={styles.raceStat}>
            <span className={styles.raceStatValue}>{finishedCount} / {totalCount}</span>
            <span className={styles.raceStatLabel}>Finished</span>
          </div>
          <div className={styles.raceStat}>
            <span className={styles.raceStatValue}>{room?.question_count || 0}</span>
            <span className={styles.raceStatLabel}>Questions</span>
          </div>
        </div>
      </div>

      <div className={styles.leaderboardTable}>
        <div className={styles.leaderboardHeaderRowSimple}>
          <span className={styles.colRankSimple}>#</span>
          <span className={styles.colNameSimple}>Name</span>
          <span className={styles.colProgressSimple}>Progress</span>
        </div>

        {leaderboard.map((entry) => {
          const app = getAppById(entry.participant.app_name);
          // Use correctCount for progress - only correct answers count towards the goal
          const progress = room
            ? (entry.correctCount / room.question_count) * 100
            : 0;
          const questionCount = room?.question_count || 0;

          return (
            <div
              key={entry.participant.id}
              className={`${styles.leaderboardRowSimple} ${entry.isFinished ? styles.leaderboardRowFinished : ''}`}
            >
              <span className={styles.colRankSimple}>
                <span className={`${styles.rankBadgeSimple} ${entry.rank <= 3 ? styles[`rank${entry.rank}`] : ''}`}>
                  {entry.rank}
                </span>
              </span>
              <span className={styles.colNameSimple}>{entry.participant.username}</span>
              <span className={styles.colProgressSimple}>
                <div className={styles.progressBarLarge}>
                  <div
                    className={styles.progressBarFillGradient}
                    style={{
                      width: `${progress}%`,
                      '--gradient-start': app?.gradientStart || '#00d5ee',
                      '--gradient-end': app?.gradientEnd || '#00ffba',
                    } as React.CSSProperties}
                  />
                  <span className={styles.progressTextOverlay}>
                    {entry.correctCount} / {questionCount}
                  </span>
                </div>
              </span>
            </div>
          );
        })}

        {leaderboard.length === 0 && (
          <div className={styles.leaderboardEmpty}>
            Waiting for participants to answer questions...
          </div>
        )}
      </div>

      <div className={styles.leaderboardActions}>
        <button onClick={onEndRace} className={styles.dangerButton}>
          End Race
        </button>
      </div>
    </div>
  );
}
